'use client';
import { useEffect, useState } from 'react';
import { useMediaQuery, Theme, Box } from '@mui/material';
import { Profile } from './components';
import { useDispatch, useSelector } from 'react-redux';
import { settingsView, showActualSection } from '@/store/actions';
import { authSelector } from '@/store/selectors';
import { UseFirstRender } from '@/hooks';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { LOGIN_USER_ASYNC } from '@/store/auth/action-types';
import { actionObject, fallbackRestUrl } from '@/utils';

export const Settings = () => {
  const [step, setStep] = useState(1);
  const router = useRouter();
  const dispatch = useDispatch();
  const { user } = useSelector(authSelector);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [isVerifyingHandoff, setIsVerifyingHandoff] = useState(false);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  useEffect(() => {
    dispatch(showActualSection('my_profile'));
  }, []);

  UseFirstRender(() => {
    const verifyHandoffToken = async (handoffToken: string) => {
      try {
        setIsVerifyingHandoff(true);
        const apiUrl = process.env.REST_URL || fallbackRestUrl;
        const response = await fetch(`${apiUrl}/auth/handoff-token-verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ handoff_token: handoffToken }),
        });
        const data = await response.json();
        const verifiedUser = data?.result;

        if (response.ok && verifiedUser?.token) {
          const verifiedEmail = verifiedUser?.email?.toLowerCase?.().trim?.();
          const currentUserEmail = user?.email?.toLowerCase?.().trim?.();

          if (verifiedEmail && currentUserEmail && verifiedEmail === currentUserEmail) {
            dispatch(settingsView(user.id));
            await router.replace('/app/profile');
            return;
          }

          dispatch(actionObject(LOGIN_USER_ASYNC, verifiedUser));
          await router.replace('/app/profile');
          return;
        }
      } catch {
        // On any verify error, fallback to the existing unauthenticated flow.
      } finally {
        setIsVerifyingHandoff(false);
      }
      router.push('/app/register');
    };

    if (!router.isReady || isVerifyingHandoff) return;
    const handoff = typeof router.query.handoff === 'string' ? router.query.handoff : null;
    if (handoff) {
      verifyHandoffToken(handoff);
      return;
    }

    if (user?.token) {
      dispatch(settingsView(user.id));
      return;
    }

    router.push('/app/register');
  }, [dispatch, isVerifyingHandoff, router.isReady, router.query.handoff, user?.email, user?.id, user?.token]);

  if (!mounted)
    return (
      <Box width={'100%'} height={'80vh'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <Image src='/images/Loading.svg' quality={'100'} alt='loading' fill style={{ objectFit: 'cover' }} />
      </Box>
    );

  return (
    <Box
      display={'flex'}
      paddingBottom={'2rem'}
      width={'100%'}
      margin={'0 auto'}
      height={'auto'}
      minHeight={'100vh'}
      textOverflow={'auto'}
      paddingTop={!isMobile ? '10rem' : '2rem'}
      justifyContent={'flex-start'}
      px={2}
      sx={{
        backgroundImage: !isMobile ? 'url("/images/thread.svg")' : 'none',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      flexDirection={!isMobile ? 'row' : 'column'}
      alignItems={'flex-start'}>
      <Profile />
    </Box>
  );
};
